import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, Plus, X } from "lucide-react";
import { navigation } from "../../../data/navigation";
import logo from "../../../assets/layout/logo.webp";
import "./header.css";

const HOVER_CLOSE_DELAY = 350;

// ---- Active page helpers -------------------------------------------------
const normalizePath = (path = "") =>
  (path.replace(/\/+$/, "") || "/").toLowerCase();

// Does this nav entry point at the page we are on? Entries with an `href`
// match the page itself and anything beneath it (e.g. a blog post keeps
// "Blogs" active). Pass `exact` to match only the page itself.
function matches(item, current, { exact = false } = {}) {
  if (!item?.href || item.external) return false;

  const href = normalizePath(item.href);

  if (current === href) return true;

  return !exact && href !== "/" && current.startsWith(`${href}/`);
}

// A dropdown menu is active when any link inside it is the current page.
function sectionIsActive(item, current) {
  if (matches(item, current) || matches(item.overview, current)) return true;

  return (item.columns || []).some(
    (column) =>
      matches(column, current) ||
      column.items.some((subItem) => matches(subItem, current)),
  );
}

function Header() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef(null);
  const closeTimer = useRef(null);
  const menuId = useId();
  const { pathname } = useLocation();
  const current = normalizePath(pathname);

  const activeSection =
    navigation.find(
      (item) => item.columns?.length && sectionIsActive(item, current),
    )?.label ?? null;

  const activeClass = (item, exact = false) =>
    matches(item, current, { exact }) ? " is-active" : "";

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimeout(closeTimer.current);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const closeMenus = () => {
    clearTimeout(closeTimer.current);
    setOpenMenu(null);
    setMobileOpen(false);
  };

  const toggleMenu = (label) => {
    clearTimeout(closeTimer.current);
    setOpenMenu((current) => (current === label ? null : label));
  };

  const openOnHover = (label) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), HOVER_CLOSE_DELAY);
  };

  // Internal links use React Router; external links open in a new tab.
  const renderAnchor = (item, className, children, key, exact = false) => {
    if (item.external) {
      return (
        <a
          key={key}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={closeMenus}
        >
          {children}
        </a>
      );
    }

    const isExactMatch = matches(item, current, { exact: true });
    const isActive = exact ? isExactMatch : matches(item, current);

    return (
      <Link
        key={key}
        to={item.href}
        className={`${className}${isActive ? " is-active" : ""}`}
        aria-current={isExactMatch ? "page" : isActive ? "true" : undefined}
        onClick={closeMenus}
      >
        {children}
      </Link>
    );
  };

  // Desktop mega-menu item: title + optional description.
  const renderMegaLink = (item) =>
    renderAnchor(
      item,
      "header-mega-link",
      <>
        <span className="header-mega-link-title">
          {item.label}
          {item.external && (
            <ArrowUpRight size={14} strokeWidth={1.6} aria-hidden="true" />
          )}
        </span>
        {item.description && (
          <span className="header-mega-link-desc">{item.description}</span>
        )}
      </>,
      `${item.label}-${item.href}`
    );

  // Mobile submenu item: title only.
  const renderMobileLink = (item) =>
    renderAnchor(
      item,
      "mobile-submenu-link",
      <>
        <span>{item.label}</span>
        {item.external && (
          <ArrowUpRight size={14} strokeWidth={1.6} aria-hidden="true" />
        )}
      </>,
      `${item.label}-${item.href}`
    );

  const renderMegaColumn = (column) => (
    <div className="header-mega-col" key={column.heading}>
      {column.href ? (
        <Link
          to={column.href}
          className={`header-mega-heading header-mega-heading-link${activeClass(column, true)}`}
          onClick={closeMenus}
        >
          <span>{column.heading}</span>
          <ArrowUpRight size={12} strokeWidth={2} aria-hidden="true" />
        </Link>
      ) : (
        <span className="header-mega-heading">{column.heading}</span>
      )}

      <div className="header-mega-list">
        {column.items.map((subItem) => renderMegaLink(subItem))}
      </div>
    </div>
  );

  const renderMobileGroup = (column) => (
    <div className="mobile-submenu-group" key={column.heading}>
      {column.href ? (
        <Link
          to={column.href}
          className={`mobile-submenu-heading mobile-submenu-heading-link${activeClass(column, true)}`}
          onClick={closeMenus}
        >
          <span>{column.heading}</span>
          <ArrowUpRight size={12} strokeWidth={2} aria-hidden="true" />
        </Link>
      ) : (
        <span className="mobile-submenu-heading">{column.heading}</span>
      )}
      {column.items.map((subItem) => renderMobileLink(subItem))}
    </div>
  );

  // Optional "overview" link (e.g. the main Industries page) shown inside a dropdown.
  const renderMegaOverview = (overview) =>
    renderAnchor(
      overview,
      "header-mega-overview",
      <>
        <span className="header-mega-overview-text">
          <span className="header-mega-overview-title">{overview.label}</span>
          {overview.description && (
            <span className="header-mega-overview-desc">{overview.description}</span>
          )}
        </span>
        <ArrowUpRight size={12} strokeWidth={2} aria-hidden="true" />
      </>,
      "overview",
      true
    );

  const renderMobileOverview = (overview) =>
    renderAnchor(
      overview,
      "mobile-submenu-heading mobile-submenu-heading-link",
      <>
        <span>{overview.label}</span>
        <ArrowUpRight size={12} strokeWidth={2} aria-hidden="true" />
      </>,
      "overview",
      true
    );

  // Top-level label as a link (only when the item has its own href).
  const renderParentLink = (item, className) => {
    if (item.external) {
      return (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={closeMenus}
        >
          {item.label}
        </a>
      );
    }

    return (
      <Link to={item.href} className={className} onClick={closeMenus}>
        {item.label}
      </Link>
    );
  };

  return (
    <header ref={headerRef} className="site-header">
      <div className="header-inner mx-auto flex min-h-[76px] w-[calc(100%-3rem)] max-w-[1720px] items-center justify-between gap-6 max-[600px]:min-h-[66px] max-[600px]:w-[calc(100%-2rem)]">
        <Link
          to="/"
          aria-label="Qualitas Global home"
          className="header-logo shrink-0"
          onClick={closeMenus}
        >
          <img src={logo} alt="Qualitas Global" />
        </Link>

        <nav
          className="desktop-nav ml-auto flex min-w-0 items-center justify-end gap-[clamp(1rem,2vw,2.25rem)]"
          aria-label="Main navigation"
        >
          {navigation.map((item, index) => {
            if (!item.columns?.length) {
              return (
                <ReactNavLink key={`${item.label}-${item.href}`} item={item} onClick={closeMenus} />
              );
            }

            const isOpen = openMenu === item.label;
            const isActiveSection = activeSection === item.label;
            const panelId = `${menuId}-desktop-${index}`;

            return (
              <div
                key={item.label}
                className={`header-nav-item ${isOpen ? "is-open" : ""}${isActiveSection ? " is-active" : ""}`}
                onMouseEnter={() => openOnHover(item.label)}
                onMouseLeave={scheduleClose}
              >
                {item.href ? (
                  <div className="header-nav-parent">
                    {renderParentLink(item, "header-nav-link header-nav-parent-link")}
                    <button
                      type="button"
                      className="header-nav-link header-nav-trigger"
                      aria-label={`Toggle ${item.label} menu`}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggleMenu(item.label)}
                      onFocus={() => setOpenMenu(item.label)}
                    >
                      <Plus size={13} strokeWidth={1.6} className="header-plus" aria-hidden="true" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="header-nav-link header-nav-trigger header-nav-label"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleMenu(item.label)}
                    onFocus={() => setOpenMenu(item.label)}
                  >
                    <span>{item.label}</span>
                    <Plus size={13} strokeWidth={1.6} className="header-plus" aria-hidden="true" />
                  </button>
                )}

                <div id={panelId} className="header-mega" hidden={!isOpen}>
                  <div className="header-mega-inner">
                    <div
                      className="header-mega-grid"
                      style={{ "--mega-cols": item.columns.length }}
                    >
                      {item.columns.map(renderMegaColumn)}
                    </div>
                    {item.overview && renderMegaOverview(item.overview)}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="header-actions flex shrink-0 items-center">
          <Link
            to="/contact-us"
            className="btn btn-primary header-cta header-cta-desktop"
            onClick={closeMenus}
          >
            <span>Contact Us</span>
            <ArrowUpRight size={16} strokeWidth={1.8} aria-hidden="true" />
          </Link>

          <button
            type="button"
            className="mobile-menu-toggle"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls={`${menuId}-mobile`}
            onClick={() => {
              // When opening, expand the section that holds the current page.
              setOpenMenu(mobileOpen ? null : activeSection);
              setMobileOpen((isOpen) => !isOpen);
            }}
          >
            {mobileOpen ? <X size={23} strokeWidth={1.6} /> : <Menu size={23} strokeWidth={1.6} />}
          </button>
        </div>
      </div>

      <div
        id={`${menuId}-mobile`}
        className={`mobile-nav ${mobileOpen ? "is-open" : ""}`}
        aria-hidden={!mobileOpen}
        inert={!mobileOpen}
      >
        <nav className="mobile-nav-inner" aria-label="Mobile navigation">
          {navigation.map((item, index) => {
            if (!item.columns?.length) {
              return <ReactNavLink key={`${item.label}-${item.href}`} item={item} className="mobile-nav-link" onClick={closeMenus} showArrow />;
            }

            const isOpen = openMenu === item.label;
            const isActiveSection = activeSection === item.label;
            const submenuId = `${menuId}-mobile-submenu-${index}`;

            return (
              <div className="mobile-nav-group" key={item.label}>
                {item.href ? (
                  <div className="mobile-nav-parent">
                    {renderParentLink(item, `mobile-nav-link mobile-nav-parent-link${isActiveSection ? " is-active" : ""}`)}
                    <button
                      type="button"
                      className="mobile-nav-trigger mobile-nav-parent-toggle"
                      aria-label={`Toggle ${item.label} submenu`}
                      aria-expanded={isOpen}
                      aria-controls={submenuId}
                      onClick={() => toggleMenu(item.label)}
                    >
                      <Plus size={16} className={`mobile-plus ${isOpen ? "is-open" : ""}`} aria-hidden="true" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`mobile-nav-trigger${isActiveSection ? " is-active" : ""}`}
                    aria-expanded={isOpen}
                    aria-controls={submenuId}
                    onClick={() => toggleMenu(item.label)}
                  >
                    <span>{item.label}</span>
                    <Plus size={16} className={`mobile-plus ${isOpen ? "is-open" : ""}`} aria-hidden="true" />
                  </button>
                )}

                <div id={submenuId} className="mobile-submenu" hidden={!isOpen}>
                  {item.overview && renderMobileOverview(item.overview)}
                  {item.columns.map(renderMobileGroup)}
                </div>
              </div>
            );
          })}

          <Link to="/contact-us" className="btn btn-primary mobile-cta" onClick={closeMenus}>
            <span>Contact Us</span>
            <ArrowUpRight size={16} strokeWidth={1.8} aria-hidden="true" />
          </Link>
        </nav>
      </div>
    </header>
  );
}

function ReactNavLink({ item, className = "header-nav-link", onClick, showArrow = false }) {
  const { pathname } = useLocation();
  const current = normalizePath(pathname);
  const isExactMatch = matches(item, current, { exact: true });
  const isActive = matches(item, current);

  const content = (
    <>
      <span>{item.label}</span>
      {(item.external || showArrow) && <ArrowUpRight size={16} aria-hidden="true" />}
    </>
  );

  if (item.external) {
    return <a href={item.href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>{content}</a>;
  }

  return (
    <Link
      to={item.href}
      className={`${className}${isActive ? " is-active" : ""}`}
      aria-current={isExactMatch ? "page" : isActive ? "true" : undefined}
      onClick={onClick}
    >
      {content}
    </Link>
  );
}

export default Header;
